/**
 * Client tool: create_support_issue
 *
 * This wraps the backend tool by getting the current context and thread ID,
 * then calling the backend with enriched parameters.
 *
 * Params JSON Schema:
 * {
 *   "$schema": "http://json-schema.org/draft-07/schema#",
 *   "title": "CreateSupportIssue Tool Params",
 *   "name": "create_support_issue",
 *   "description": "Creates a support issue when user needs human assistance " \
 *                  "or wants to submit a ticket",
 *   "parameters": {
 *     "type": "object",
 *     "properties": {
 *       "title": {
 *         "type": "string",
 *         "description": "A clear summary of the problem or request"
 *       },
 *       "description": {
 *         "type": "string",
 *         "description": "Detailed description of the issue, " \
 *                        "including context from the conversation"
 *       },
 *       "chat_kit_thread_id": {
 *         "type": "string",
 *         "description": "Optional: ChatKit thread ID to link conversation"
 *       },
 *       "organization_id": {
 *         "type": "string",
 *         "description": "Optional: Organization ID if issue is org-specific"
 *       }
 *     },
 *     "required": ["title", "description"],
 *     "additionalProperties": true
 *   }
 * }
 *
 * Returns JSON Schema:
 * {
 *   "$schema": "http://json-schema.org/draft-07/schema#",
 *   "title": "CreateSupportIssue Tool Result",
 *   "type": "object",
 *   "properties": {
 *     "id": { "type": "string" },
 *     "title": { "type": "string" },
 *     "description": { "type": "string" }
 *   },
 *   "required": ["id", "title", "description"],
 *   "additionalProperties": false
 * }
 */
import { getCsrfToken } from './getCsrfToken.js';
import { getCurrentContext } from './getCurrentContext.js';
import { getLocale } from './getLocale.js';

/**
 * Get localized messages for support issue widget
 */
export const getSupportIssueMessages = (locale: string) => {
  if (locale === 'sv') {
    return {
      titleMissing: 'Rubrik saknas. Vänligen ange en rubrik för supportärendet.',
      descriptionMissing: 'Beskrivning saknas. Vänligen ange en beskrivning för supportärendet.',
      createError: 'Ett fel uppstod vid skapandet av supportärendet. Vänligen försök igen.',
      successMessage: 'Ditt supportärende har registrerats. Vi återkommer så snart som möjligt.'
    };
  }

  return {
    titleMissing: 'Title is missing. Please provide a title for the support issue.',
    descriptionMissing: 'Description is missing. Please provide a description for the support issue.',
    createError: 'An error occurred while creating the support issue. Please try again.',
    successMessage: 'Your support issue has been registered. We will get back to you as soon as possible.'
  };
};

export const createSupportIssue = async (
  params: {
    title: string;
    description: string;
    chat_kit_thread_id?: string;
    organization_id?: string;
  },
  threadId?: string | null
) => {
  // Get current context to enrich with organization_id if available
  let context: {
    organization_id?: string | null;
  } = {};

  try {
    const currentContext = await getCurrentContext();
    context = {
      organization_id: currentContext.organization_id
    };
  } catch (error) {
    // If context lookup fails, continue without it
    console.warn('[ChatKit] Failed to get current context:', error);
  }

  // Build enriched params
  const enrichedParams: {
    title: string;
    description: string;
    chat_kit_thread_id?: string;
    organization_id?: string;
  } = {
    title: params.title,
    description: params.description
  };

  // Add thread ID if available (from ChatKit or params)
  if (threadId) {
    enrichedParams.chat_kit_thread_id = threadId;
  } else if (params.chat_kit_thread_id) {
    enrichedParams.chat_kit_thread_id = params.chat_kit_thread_id;
  }

  // Add organization_id from context if available (context takes precedence over params)
  if (context.organization_id) {
    enrichedParams.organization_id = context.organization_id;
  } else if (params.organization_id) {
    enrichedParams.organization_id = params.organization_id;
  }

  const response = await fetch('/chat_kit_tools/create_support_issue/call', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': getCsrfToken()
    },
    credentials: 'same-origin',
    body: JSON.stringify(enrichedParams)
  });

  if (!response.ok) {
    let errorMessage = `Failed to create support issue: ${response.status}`;
    try {
      const json = await response.json();
      errorMessage = json?.error || errorMessage;
    } catch {
      // Keep default error message
    }
    throw new Error(errorMessage);
  }

  return await response.json();
};

/**
 * Create support issue from widget action
 * Handles validation, creation, and user feedback for widget-based support issue creation
 */
export const createSupportIssueFromWidget = async (
  actionPayload: {
    supportIssue?: {
      title?: string;
      description?: string;
    };
  },
  threadId?: string | null
): Promise<void> => {
  const title = actionPayload?.supportIssue?.title ?? actionPayload?.title;
  const description =
    actionPayload?.supportIssue?.description ?? actionPayload?.description;

  // Get current locale and messages
  const locale = getLocale();
  const messages = getSupportIssueMessages(locale);

  // Validate required fields
  if (!title) {
    throw new Error(messages.titleMissing);
  }

  if (!description) {
    throw new Error(messages.descriptionMissing);
  }

  // Create support issue
  const result = await createSupportIssue(
    {
      title,
      description
    },
    threadId
  );

  // Handle errors
  if (result.error) {
    throw new Error(result.error);
  }

  // Handle success
  if (result.support_issue || result.id) {
    // Support issue successfully created.
    //
    // Present the user with a success message. Currently, providing
    // direct feedback via chat messages is not supported by ChatKit
    // without implementing a custom backend solution.
    window.alert(messages.successMessage);
    return;
  }

  // Handle unexpected result
  throw new Error(messages.createError);
};
